import * as THREE from "three";
import { Context } from "../core/context";

import {
  DrawOperationMovePointBeforeParams,
  DrawOperationMovePointParams,
  IModifyOperation,
  ModifyOperateState,
} from "./IModifyOperation";

import {
  CommonColor,
  DimensionsAnnotationContinuousModifyReturnData,
  DimensionsAnnotationContinuousModifyTempObjectData,
  DimensionsAnnotationModifyReturnData,
} from "@/core/Parameters";
import { TextModel } from "@/core/TextModel";

class DimensionsAnnotationContinuousModify implements IModifyOperation {
  private m_context: Context;
  private m_curPoint: THREE.Vector3 = new THREE.Vector3();

  private m_locationObject: THREE.Line;
  private m_tempObjectGroup: THREE.Group = new THREE.Group();
  private m_tempLineObject: THREE.LineSegments | undefined;
  private m_returnData:
    | DimensionsAnnotationContinuousModifyReturnData
    | undefined;
  private m_points: THREE.Vector3[] = [];
  private m_textModel: TextModel;
  private m_curText: THREE.Mesh | undefined;
  private m_curDistance: string = "";
  private m_tempLinePoints: THREE.Vector3[] = [];
  private m_auxLine: THREE.Line | undefined;
  private m_auxStartPoint: THREE.Vector3 | undefined;
  private m_isContinue = false;
  private m_tempObjectDatas: DimensionsAnnotationContinuousModifyTempObjectData[] =
    [];
  private m_originTextMatrix: THREE.Matrix4 = new THREE.Matrix4();
  private m_textDirV: THREE.Vector3 = new THREE.Vector3();
  private m_tempLineIndexs: number[] = [0, 1, 1, 2, 2, 3];
  private m_baseLine3: THREE.Line3 | undefined;
  private m_textsGroup: THREE.Group = new THREE.Group();
  constructor(context: Context) {
    this.m_context = context;
    this.m_locationObject = context.ToolsManager.CreatedLocationLine();
    this.m_context.SceneManager.AuxModel.add(this.m_tempObjectGroup);
    this.m_textModel = new TextModel(this.m_context);
    this.m_tempObjectGroup.add(this.m_textsGroup);
  }

  OnMovePointBefore(params: DrawOperationMovePointBeforeParams): void {}
  OnMovePoint(point: DrawOperationMovePointParams): void {
    this.m_curPoint = point.point;
    this.m_locationObject.position.copy(this.m_curPoint);
    this.updateTempObject();
    if (this.m_isContinue && this.m_auxStartPoint) {
      this.m_auxLine?.geometry.setFromPoints([
        this.m_auxStartPoint,
        this.m_curPoint,
      ]);
      this.m_auxLine?.computeLineDistances();
    }
  }
  OnDownPoint(point: DrawOperationMovePointParams): void {
    if (!this.m_points.length) {
      this.createTempLine();
    }

    if (this.m_isContinue) {
      this.m_auxStartPoint = point.point;
      this.computeTempObject();
    } else {
      this.m_points.push(point.point);
      if (this.m_points.length == 2) {
        this.m_curDistance = (this.m_points[0].distanceTo(point.point) * 1000)
          .toFixed(2)
          .toString();
        this.m_curText = this.m_textModel.CreateText({
          text: this.m_curDistance,
        });
        this.m_textsGroup.add(this.m_curText);
        this.m_originTextMatrix = this.getTextMatrix(
          this.m_points[0].clone(),
          point.point.clone()
        );
        this.m_curText.applyMatrix4(this.m_originTextMatrix.clone());
        this.m_curText.visible = false;
        this.m_auxLine = this.m_context.ToolsManager.CreatedAuxLine();
      } else if (this.m_points.length == 3) {
        this.m_isContinue = true;
        this.m_auxStartPoint = this.m_points[1];
        this.m_tempObjectDatas[0] = {
          textInfo: {
            text: this.m_curDistance,
            textPosition: this.m_curText?.position!,
          },
          lineObj: this.m_tempLineObject!,
          linePoints: this.m_tempLinePoints,
          dropPoint: undefined,
        };
        this.m_returnData = {
          startPoint: this.m_points[0],
          endPoint: this.m_points[1],
          fallPoint: this.m_curText!.position!.clone(),
          points: [],
        };
        this.m_baseLine3 = new THREE.Line3(
          this.m_tempLinePoints[1],
          this.m_tempLinePoints[2]
        );
        this.m_curDistance = "";
        return;
      }
    }
  }
  onKeyDown(e: KeyboardEvent): void {}
  onKeyUp(e: KeyboardEvent): void {}

  OnZoom(camera: THREE.Camera): void {}

  Finish(operate: ModifyOperateState) {
    if (this.m_returnData) {
      let { startPoint, endPoint } = this.m_returnData;
      for (let i = 0; i < this.m_tempObjectDatas.length; i++) {
        let { dropPoint } = this.m_tempObjectDatas[i];
        if (
          dropPoint &&
          !dropPoint.equals(startPoint) &&
          !dropPoint.equals(endPoint)
        ) {
          this.m_returnData?.points.push(dropPoint);
        }
      }
    }
    this.clear();
    if (operate == ModifyOperateState.complete) {
      return this.m_returnData;
    } else if (operate == ModifyOperateState.cancel) {
      return [];
    }
  }
  OnWheel(e: WheelEvent): void {}

  onMouseMove(e?: PointerEvent): void {}

  onMouseDown(e: PointerEvent): void {
    if (e.button !== 0) return;
  }

  onMouseUp(e: PointerEvent): void {
    if (e.button !== 0) return;
  }

  private createTempLine() {
    let geo = new THREE.BufferGeometry();
    let mat = new THREE.LineBasicMaterial();
    this.m_tempLineObject = new THREE.LineSegments(geo, mat);
    this.m_tempLineObject.frustumCulled = false;
    this.m_tempObjectGroup.add(this.m_tempLineObject);
  }

  private updateTempObject() {
    if (this.m_points.length) {
      let zAxis = new THREE.Vector3(0, 0, 1);
      let auxStartPoint = this.m_points[0];
      let auxEndPoint = this.m_curPoint;
      let vector1 = new THREE.Vector3();
      let vector2 = new THREE.Vector3();

      if (this.m_points.length == 2) {
        auxEndPoint = this.m_points[1];
        let line3 = new THREE.Line3(auxStartPoint, auxEndPoint);
        let closestLine3ToCurrentPoint = new THREE.Vector3();
        line3.closestPointToPoint(
          this.m_curPoint,
          false,
          closestLine3ToCurrentPoint
        );

        let closestLine3ToCurrentPointDistance =
          closestLine3ToCurrentPoint.distanceTo(this.m_curPoint);
        vector1 = auxStartPoint.clone().sub(auxEndPoint).normalize();
        vector2 = this.m_curPoint.clone().sub(auxEndPoint).normalize();
        let cross = vector2.clone().cross(vector1).z;

        if (cross > 0) {
          auxStartPoint = auxStartPoint.clone().add(
            vector1
              .clone()
              .negate()
              .applyAxisAngle(zAxis, Math.PI / 2)
              .setLength(closestLine3ToCurrentPointDistance)
          );
          auxEndPoint = auxEndPoint.clone().add(
            vector1
              .clone()
              .applyAxisAngle(zAxis, -Math.PI / 2)
              .setLength(closestLine3ToCurrentPointDistance)
          );
        } else {
          auxStartPoint = auxStartPoint.clone().add(
            vector1
              .clone()
              .negate()
              .applyAxisAngle(zAxis, -Math.PI / 2)
              .setLength(closestLine3ToCurrentPointDistance)
          );
          auxEndPoint = auxEndPoint.clone().add(
            vector1
              .clone()
              .applyAxisAngle(zAxis, Math.PI / 2)
              .setLength(closestLine3ToCurrentPointDistance)
          );
        }
        if (this.m_curText) {
          this.m_curText.visible = true;
          this.m_textDirV = new THREE.Vector3()
            .subVectors(this.m_points[0], auxStartPoint)
            .normalize()
            .negate();
          let cP = new THREE.Vector3()
            .lerpVectors(auxStartPoint, auxEndPoint, 0.5)
            .add(this.m_textDirV.setLength(0.25));
          this.m_curText.position.copy(cP);
        }
        this.m_tempLinePoints = [
          this.m_points[0],
          auxStartPoint,
          auxEndPoint,
          this.m_points[1],
        ];
        this.m_tempLineObject?.geometry.setFromPoints(this.m_tempLinePoints);
        this.m_tempLineObject?.geometry.setIndex(this.m_tempLineIndexs);
      } else if (this.m_points.length == 1) {
        this.m_tempLineObject?.geometry.setFromPoints([
          auxStartPoint,
          auxEndPoint,
        ]);
      }
    }
  }

  private getTextMatrix(startP: THREE.Vector3, endP: THREE.Vector3) {
    let XAxis = new THREE.Vector3(1, 0, 0);
    let zAxis = new THREE.Vector3(0, 0, 1);
    let CenterP = startP.clone().add(endP).multiplyScalar(0.5);
    let v = endP.clone().sub(startP).normalize();
    let angle = v.angleTo(XAxis);
    if (v.clone().cross(XAxis).z > 0) {
      angle = -angle;
    }
    if (Math.abs(angle) > Math.PI / 2) {
      angle = angle - Math.PI;
    }
    let q = new THREE.Quaternion().setFromAxisAngle(zAxis, angle);
    let s = new THREE.Vector3(1, 1, 1);
    return new THREE.Matrix4().compose(CenterP, q, s);
  }
  private computeTempObject() {
    let xAxis = new THREE.Vector3(1, 0, 0);
    let closestLine3ToCurrentPoint = new THREE.Vector3();
    this.m_baseLine3!.closestPointToPoint(
      this.m_curPoint,
      false,
      closestLine3ToCurrentPoint
    );
    // 判断点是否在之前的线段之间
    let closePointIndex = -1;
    for (let i = 0; i < this.m_tempObjectDatas.length; i++) {
      let { linePoints } = this.m_tempObjectDatas[i];
      let v1 = linePoints[1].clone().sub(closestLine3ToCurrentPoint);
      let v2 = linePoints[2].clone().sub(closestLine3ToCurrentPoint);
      if (v1.clone().dot(v2) < 0 && Math.abs(v1.clone().cross(v2).z) < 0.01) {
        closePointIndex = i;
        break;
      }
    }

    // 在最前面或者最后面新增
    if (closePointIndex == -1) {
      let { linePoints } =
        this.m_tempObjectDatas[this.m_tempObjectDatas.length - 1];
      let v1 = linePoints[2].clone().sub(linePoints[3]);
      let v2 = this.m_curPoint.clone().sub(linePoints[3]);

      this.createTempLine();

      let tempLineEndPoint = closestLine3ToCurrentPoint
        .clone()
        .add(v1.clone().negate().setLength(v1.length()));
      let tempLinePoints: THREE.Vector3[] = [];

      let crossResult = v1.clone().cross(xAxis).z;
      console.log(crossResult, "crossResult");
      if (crossResult < 0) {
        if (v2.clone().cross(v1).z < 0) {
          linePoints = this.m_tempObjectDatas[0].linePoints;
          tempLinePoints = [
            tempLineEndPoint,
            closestLine3ToCurrentPoint,
            linePoints[1],
            linePoints[0],
          ];
        } else {
          tempLinePoints = [
            linePoints[3],
            linePoints[2],
            closestLine3ToCurrentPoint,
            tempLineEndPoint,
          ];
        }
      } else {
        if (v2.clone().cross(v1).z < 0) {
          linePoints = this.m_tempObjectDatas[0].linePoints;
          tempLinePoints = [
            linePoints[3],
            linePoints[2],
            closestLine3ToCurrentPoint,
            tempLineEndPoint,
          ];
        } else {
          tempLinePoints = [
            tempLineEndPoint,
            closestLine3ToCurrentPoint,
            linePoints[1],
            linePoints[0],
          ];
        }
      }

      // if(crossResult>0){
      //   tempLinePoints = [
      //     tempLineEndPoint,
      //     closestLine3ToCurrentPoint,
      //     linePoints[2],
      //     linePoints[3],
      //   ];
      // }
      this.m_tempLineObject?.geometry.setFromPoints(tempLinePoints);
      this.m_tempLineObject?.geometry.setIndex(this.m_tempLineIndexs);
      this.m_curDistance = (
        tempLinePoints[1].distanceTo(tempLinePoints[2]) * 1000
      )
        .toFixed(2)
        .toString();
      this.m_curText = this.m_textModel.CreateText({
        text: this.m_curDistance,
      });
      this.m_textsGroup.add(this.m_curText);
      let textPosition = this.getTextPosition(
        tempLinePoints[1],
        tempLinePoints[2]
      );
      this.setTextPosition(textPosition);

      if (v2.clone().cross(v1).z < 0) {
        this.m_tempObjectDatas.unshift({
          linePoints: tempLinePoints,
          textInfo: {
            text: this.m_curDistance,
            textPosition,
          },
          lineObj: this.m_tempLineObject!,
          dropPoint: tempLineEndPoint,
        });
      } else {
        this.m_tempObjectDatas.push({
          linePoints: tempLinePoints,
          textInfo: {
            text: this.m_curDistance,
            textPosition,
          },
          lineObj: this.m_tempLineObject!,
          dropPoint: tempLineEndPoint,
        });
      }
    } else {
      // 在中间需要删除被插入的数据，新增两个数据
      let { linePoints } = this.m_tempObjectDatas[closePointIndex];
      this.createTempLine();
      let v = linePoints[0].clone().sub(linePoints[1]);
      let tempLineEndPoint1 = closestLine3ToCurrentPoint
        .clone()
        .add(v.clone().setLength(v.length()));
      let tempLinePoints1 = [
        linePoints[0],
        linePoints[1],
        closestLine3ToCurrentPoint,
        tempLineEndPoint1,
      ];
      this.m_tempLineObject?.geometry.setFromPoints(tempLinePoints1);
      this.m_tempLineObject?.geometry.setIndex(this.m_tempLineIndexs);
      this.m_curDistance = (
        tempLinePoints1[1].distanceTo(tempLinePoints1[2]) * 1000
      )
        .toFixed(2)
        .toString();
      let textPosition1 = this.getTextPosition(
        tempLinePoints1[1],
        tempLinePoints1[2]
      );
      let tempObjectData1 = {
        linePoints: tempLinePoints1,
        textInfo: {
          text: this.m_curDistance,
          textPosition: textPosition1,
        },
        lineObj: this.m_tempLineObject!.clone(),
        dropPoint: tempLineEndPoint1,
      };

      this.createTempLine();
      v = linePoints[3].clone().sub(linePoints[2]);
      let tempLineEndPoint2 = closestLine3ToCurrentPoint
        .clone()
        .add(v.clone().setLength(v.length()));
      let tempLinePoints2 = [
        tempLineEndPoint2,
        closestLine3ToCurrentPoint,
        linePoints[2],
        linePoints[3],
      ];
      this.m_tempLineObject?.geometry.setFromPoints(tempLinePoints2);
      this.m_tempLineObject?.geometry.setIndex(this.m_tempLineIndexs);
      this.m_curDistance = (
        tempLinePoints2[1].distanceTo(tempLinePoints2[2]) * 1000
      )
        .toFixed(2)
        .toString();
      let textPosition2 = this.getTextPosition(
        tempLinePoints2[1],
        tempLinePoints2[2]
      );
      let tempObjectData2 = {
        linePoints: tempLinePoints2,
        textInfo: {
          text: this.m_curDistance,
          textPosition: textPosition2,
        },
        lineObj: this.m_tempLineObject!,
        dropPoint: linePoints[3],
      };
      let tv1 = tempLinePoints1[0].clone().sub(closestLine3ToCurrentPoint);
      let tv2 = tempLinePoints2[0].clone().sub(closestLine3ToCurrentPoint);
      if (tv1.clone().cross(tv2).z > 0) {
        this.m_tempObjectDatas.splice(
          closePointIndex,
          1,
          tempObjectData1,
          tempObjectData2
        );
      } else {
        this.m_tempObjectDatas.splice(
          closePointIndex,
          1,
          tempObjectData2,
          tempObjectData1
        );
      }

      this.upDataTextModel();
    }
  }

  private setTextPosition(
    textPosition: THREE.Vector3,
    text: THREE.Mesh = this.m_curText!
  ) {
    text.applyMatrix4(this.m_originTextMatrix.clone());
    text.position.copy(textPosition);
  }
  private getTextPosition(p1: THREE.Vector3, p2: THREE.Vector3) {
    let cP = new THREE.Vector3()
      .lerpVectors(p1, p2, 0.5)
      .add(this.m_textDirV.setLength(0.25));
    return cP;
  }
  private upDataTextModel() {
    this.m_textsGroup.removeFromParent();
    this.m_textsGroup = new THREE.Group();
    this.m_tempObjectGroup.add(this.m_textsGroup);
    for (let i = 0; i < this.m_tempObjectDatas.length; i++) {
      let { textInfo } = this.m_tempObjectDatas[i];
      let curText = this.m_textModel.CreateText({ text: textInfo.text });
      this.setTextPosition(textInfo.textPosition, curText);
      this.m_textsGroup.add(curText);
    }
  }
  private clear() {
    this.m_context.ToolsManager.GroupClear(this.m_tempObjectGroup);
    this.m_context.ToolsManager.ObjectsClear([
      this.m_locationObject,
      this.m_auxLine!,
    ]);
  }
}

export { DimensionsAnnotationContinuousModify };
